import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

async function loadCurrentMembership(userId) {
  const { data: memberships, error: membershipError } = await supabase
    .from('workspace_members')
    .select('workspace_id, role, active, created_at')
    .eq('user_id', userId)
    .eq('active', true)
    .order('created_at', { ascending: true })
    .limit(1);

  if (membershipError) throw membershipError;
  const membership = memberships?.[0] ?? null;
  if (!membership) return { membership: null, workspace: null, profile: null };

  const [{ data: workspace, error: workspaceError }, { data: profile, error: profileError }] = await Promise.all([
    supabase.from('workspaces').select('id, name').eq('id', membership.workspace_id).single(),
    supabase.from('profiles').select('id, email, display_name').eq('id', userId).single(),
  ]);

  if (workspaceError) throw workspaceError;
  if (profileError) throw profileError;

  return { membership, workspace, profile };
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [membership, setMembership] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState('');

  const refreshMembership = useCallback(async (nextSession = null) => {
    let effectiveSession = nextSession;
    if (!effectiveSession) {
      const { data } = await supabase.auth.getSession();
      effectiveSession = data.session ?? null;
    }

    if (!effectiveSession?.user?.id) {
      setMembership(null);
      setWorkspace(null);
      setProfile(null);
      return;
    }

    try {
      setAccessError('');
      const loaded = await loadCurrentMembership(effectiveSession.user.id);
      setMembership(loaded.membership);
      setWorkspace(loaded.workspace);
      setProfile(loaded.profile);
    } catch (error) {
      console.error(error);
      setAccessError('Your account is signed in, but CRM access could not be loaded.');
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      const initialSession = data.session ?? null;
      setSession(initialSession);
      if (initialSession) await refreshMembership(initialSession);
      if (mounted) setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setSession(nextSession);
      if (nextSession) await refreshMembership(nextSession);
      else {
        setMembership(null);
        setWorkspace(null);
        setProfile(null);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, [refreshMembership]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo(() => ({
    session,
    user: session?.user ?? null,
    membership,
    workspace,
    profile,
    role: membership?.role ?? null,
    loading,
    accessError,
    refreshMembership,
    signOut,
  }), [session, membership, workspace, profile, loading, accessError, refreshMembership, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider.');
  return value;
}
